"use client";

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { handleMultiSelect } from "./utils";
import type { AnalyticsFilters } from "./types";

type FiltersPanelThreeProps = {
  filters: AnalyticsFilters;
  setFilters: (next: AnalyticsFilters) => void;
  creditorRegionOptions: string[];
  borrowerRegionOptions: string[];
  creditorTypeColumns: string[];
  denominationColumns: string[];
  minYear: number;
  maxYear: number;
  sticky?: boolean;
  horizontal?: boolean;
  noCard?: boolean;
};

export default function FiltersPanelThree({
  filters,
  setFilters,
  creditorRegionOptions,
  borrowerRegionOptions,
  creditorTypeColumns,
  denominationColumns,
  minYear,
  maxYear,
  sticky = true,
  horizontal = false,
  noCard = false,
}: FiltersPanelThreeProps) {
  void creditorRegionOptions;
  void denominationColumns;

  const handleReset = () => {
    setFilters({
      selectedCreditorRegions: [],
      selectedBorrowerRegions: [],
      selectedCreditorTypes: [],
      selectedDenominations: [],
      yearRange: [minYear, maxYear],
      completeDataOnly: false,
    });
  };

  const borrowerRegionSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Borrower Region</InputLabel>
      <Select
        multiple
        value={filters.selectedBorrowerRegions}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedBorrowerRegions: next }),
          )
        }
        input={<OutlinedInput label="Borrower Region" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {borrowerRegionOptions.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedBorrowerRegions.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const creditorTypeSelect = (
    <FormControl size="small" sx={{ width: "100%" }}>
      <InputLabel>Creditor Type</InputLabel>
      <Select
        multiple
        value={filters.selectedCreditorTypes}
        onChange={(event) =>
          handleMultiSelect(event, (next) =>
            setFilters({ ...filters, selectedCreditorTypes: next }),
          )
        }
        input={<OutlinedInput label="Creditor Type" />}
        renderValue={(selected) => selected.join(", ")}
      >
        {creditorTypeColumns.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              checked={filters.selectedCreditorTypes.includes(option)}
            />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeSlider = (
    <Box sx={horizontal ? { width: 240 } : {}}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0 }}>
        Date Range: {filters.yearRange[0]} - {filters.yearRange[1]}
      </Typography>
      <Slider
        value={filters.yearRange}
        min={minYear}
        max={maxYear}
        onChange={(_, value) =>
          setFilters({ ...filters, yearRange: value as number[] })
        }
        valueLabelDisplay="auto"
      />
    </Box>
  );

  const resetButton = (
    <Button
      variant="outlined"
      // variant="contained"
      size="small"
      onClick={handleReset}
      sx={{ minWidth: 92 }}
    >
      Reset
    </Button>
  );

  const inner = horizontal ? (
    <Stack spacing={2.2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "1fr 1fr auto auto",
          },
          gap: "2rem",
          alignItems: "center",
        }}
      >
        {borrowerRegionSelect}
        {creditorTypeSelect}
        {dateRangeSlider}
        {resetButton}
      </Box>
    </Stack>
  ) : (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Filters
      </Typography>
      <Stack spacing={2.2}>
        {borrowerRegionSelect}
        {creditorTypeSelect}
        {dateRangeSlider}
        {resetButton}
      </Stack>
    </>
  );

  if (noCard) {
    return inner;
  }

  return (
    <Card
      sx={{
        height: "fit-content",
        position: sticky ? { lg: "sticky" } : "static",
        top: sticky ? { lg: 16 } : "auto",
      }}
    >
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
